import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  Chip,
  Divider,
  IconButton,
  Autocomplete,
  CircularProgress,
  Stack,
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import ModalBox from "../themes/overrides/ModalBox";
import LoadingOverlay from "./LoadingOverlay";
import { aiGenerateEntry, getEntries } from "../api/api";

const MAX_IMAGE_BYTES = 8 * 1024 * 1024; // 8MB — keep request well under the 50mb body cap

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

/**
 * AI generate-entry modal. The user writes a brief, picks one or more existing
 * entries of this content type as a style/structure reference, and (optionally)
 * uploads a screenshot. The backend drafts the text fields; nothing is created.
 *
 * On "Apply", the generated text fields are merged into the create-entry form
 * via `onApply`. Reference/media fields can't be generated (the model can't
 * invent valid entry IDs), so they are surfaced for the user to attach — the
 * "Pick existing" action hands off to the form's existing reference picker via
 * `onPickReference`.
 */
const AiGenerateEntryModal = ({
  isOpen,
  onClose,
  workspaceId,
  contentTypeId,
  fieldDefs = [],
  locale = "en",
  onApply,
  onPickReference,
  onCreateReference,
}) => {
  const [brief, setBrief] = useState("");
  const [image, setImage] = useState(null); // data URL
  const [imageName, setImageName] = useState("");
  const [examples, setExamples] = useState([]); // selected entry objects
  const [entryOptions, setEntryOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [applied, setApplied] = useState(false);
  const [creatingRefId, setCreatingRefId] = useState(null);

  const fieldNameById = React.useMemo(() => {
    const map = {};
    (fieldDefs || []).forEach((f) => {
      map[f.id] = f.name;
    });
    return map;
  }, [fieldDefs]);

  // Load existing entries of this content type to offer as style references.
  useEffect(() => {
    if (!isOpen || !workspaceId || !contentTypeId) return;
    let active = true;
    setOptionsLoading(true);
    getEntries(workspaceId, { contentTypeId, page: 1 })
      .then((res) => {
        if (!active) return;
        setEntryOptions(Array.isArray(res?.data?.entries) ? res.data.entries : []);
      })
      .catch(() => active && setEntryOptions([]))
      .finally(() => active && setOptionsLoading(false));
    return () => {
      active = false;
    };
  }, [isOpen, workspaceId, contentTypeId]);

  const reset = () => {
    setBrief("");
    setImage(null);
    setImageName("");
    setExamples([]);
    setError("");
    setResult(null);
    setApplied(false);
    setLoading(false);
    setCreatingRefId(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleImage = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Please choose an image file.");
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setError("Image is too large (max 8MB).");
      return;
    }
    setError("");
    setImageName(file.name);
    setImage(await fileToDataUrl(file));
  };

  const handleGenerate = async () => {
    if (!brief.trim() && !image) {
      setError("Write a brief (or upload a screenshot) first.");
      return;
    }
    setLoading(true);
    setError("");
    setResult(null);
    setApplied(false);
    try {
      const res = await aiGenerateEntry(workspaceId, {
        contentTypeId,
        exampleEntryIds: examples.map((e) => e._id),
        brief: brief.trim() || undefined,
        locale,
        image: image || undefined,
      });
      const data = res?.data;
      if (data?.status === "success") {
        setResult(data);
      } else {
        setError(data?.message || "Could not generate the entry. Try again.");
      }
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          "Request failed. Check the workspace AI model configuration."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    if (result?.fields) {
      onApply(result.fields);
      setApplied(true);
    }
  };

  const handlePickReference = (fieldId) => {
    // Hand off to the form's existing reference picker, then close the modal so
    // the picker (rendered by the form) is visible.
    handleClose();
    onPickReference?.(fieldId);
  };

  // Generate a NEW referenced sub-entry: draft its fields for the target content
  // type (reusing this same endpoint), then hand off so the form opens it in the
  // nested drawer, pre-filled, for the user to review and Save.
  const handleCreateWithAi = async (rf) => {
    if (!rf.targetContentTypeId) return;
    setCreatingRefId(rf.id);
    setError("");
    try {
      const baseBrief = brief.trim() || "Generate appropriate content";
      const res = await aiGenerateEntry(workspaceId, {
        contentTypeId: rf.targetContentTypeId,
        brief: `${baseBrief} — generate the "${rf.name}" for this entry.`,
        locale,
      });
      const data = res?.data;
      if (data?.status === "success") {
        handleClose();
        onCreateReference?.({
          fieldId: rf.id,
          contentTypeId: rf.targetContentTypeId,
          generatedFields: data.fields,
        });
      } else {
        setError(data?.message || "Could not draft the referenced entry. Try again.");
      }
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          "Request failed drafting the referenced entry."
      );
    } finally {
      setCreatingRefId(null);
    }
  };

  const filledFieldIds = result?.fields ? Object.keys(result.fields) : [];
  const referenceFields = result?.referenceFields || [];

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <ModalBox
        sx={{
          width: { xs: "95%", sm: "640px", md: "720px" },
          maxHeight: { xs: "95vh", sm: "90vh" },
          overflowY: "auto",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AutoAwesomeOutlinedIcon color="primary" />
            <Typography variant="h5">Generate Entry with AI</Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Describe the entry you want. Pick one or more existing entries for the AI
          to match in tone and structure. You review everything before saving.
        </Typography>

        <Alert severity="warning" sx={{ mb: 2 }}>
          Don&apos;t include real customer information — the brief and any image
          are sent to the configured AI provider.
        </Alert>

        {!result && (
          <>
            <TextField
              label="Brief — what should this entry be about?"
              multiline
              minRows={4}
              fullWidth
              value={brief}
              onChange={(e) => setBrief(e.target.value)}
              placeholder="e.g. A blog about renting mobility scooters for a Disney World family trip — tips on booking, getting around the parks, and staying comfortable."
              sx={{ mb: 2 }}
            />

            <Autocomplete
              multiple
              options={entryOptions}
              loading={optionsLoading}
              value={examples}
              onChange={(_e, val) => setExamples(val)}
              getOptionLabel={(o) => o?.identifier || "(untitled)"}
              isOptionEqualToValue={(o, v) => o._id === v._id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Reference entries (style/structure example)"
                  placeholder="Pick existing entries"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {optionsLoading ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              sx={{ mb: 2 }}
            />

            <Box
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                handleImage(e.dataTransfer.files?.[0]);
              }}
              sx={{
                border: "1px dashed",
                borderColor: "divider",
                borderRadius: 1,
                p: 2,
                textAlign: "center",
                mb: 2,
              }}
            >
              <Button component="label" startIcon={<CloudUploadOutlinedIcon />}>
                Upload a reference screenshot (optional)
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => handleImage(e.target.files?.[0])}
                />
              </Button>
              {imageName && (
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  {imageName}{" "}
                  <Button size="small" onClick={() => { setImage(null); setImageName(""); }}>
                    remove
                  </Button>
                </Typography>
              )}
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                Images require a vision model (Qwen, ChatGPT, or Gemini) as the workspace default.
              </Typography>
            </Box>
          </>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {result && (
          <Box sx={{ mb: 2 }}>
            {result.reasoning && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {result.reasoning}
              </Typography>
            )}

            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Generated fields
            </Typography>
            {filledFieldIds.length > 0 ? (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                {filledFieldIds.map((fid) => (
                  <Chip key={fid} label={fieldNameById[fid] || fid} color="primary" variant="outlined" />
                ))}
              </Box>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                The model didn&apos;t return any usable text fields. Try refining the brief.
              </Alert>
            )}

            <Button
              variant="contained"
              onClick={handleApply}
              disabled={filledFieldIds.length === 0 || applied}
              sx={{ mb: 2 }}
            >
              {applied ? "Applied to form ✓" : "Apply to form"}
            </Button>

            {referenceFields.length > 0 && (
              <>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                  References &amp; media
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  The AI recommends, per field, whether to create a new linked
                  entry or reuse an existing one. You confirm each.
                </Typography>
                <Stack spacing={1}>
                  {referenceFields.map((rf) => {
                    const canCreate =
                      rf.type === "reference" &&
                      rf.recommendation === "create" &&
                      !!rf.targetContentTypeId;
                    return (
                      <Box
                        key={rf.id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                          p: 1.5,
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            <Typography variant="body2" fontWeight={600}>
                              {rf.name}
                            </Typography>
                            {rf.type === "reference" && rf.recommendation && (
                              <Chip
                                size="small"
                                color={rf.recommendation === "create" ? "primary" : "default"}
                                label={rf.recommendation === "create" ? "AI: create new" : "AI: reuse existing"}
                              />
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {rf.targetContentTypeName ? `${rf.targetContentTypeName} · ` : ""}
                            {rf.reason || rf.type}
                          </Typography>
                        </Box>
                        {rf.type === "reference" ? (
                          <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
                            {canCreate && (
                              <Button
                                size="small"
                                variant="contained"
                                disabled={!!creatingRefId}
                                onClick={() => handleCreateWithAi(rf)}
                              >
                                {creatingRefId === rf.id ? (
                                  <CircularProgress size={16} color="inherit" />
                                ) : (
                                  "Create with AI"
                                )}
                              </Button>
                            )}
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={!!creatingRefId}
                              onClick={() => handlePickReference(rf.id)}
                            >
                              Pick existing
                            </Button>
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Upload in the form
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              </>
            )}
          </Box>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1 }}>
          {result ? (
            <>
              <Button onClick={reset}>Start over</Button>
              <Button variant="contained" onClick={handleClose}>
                Done
              </Button>
            </>
          ) : (
            <Button variant="contained" onClick={handleGenerate} disabled={loading}>
              {loading ? "Generating..." : "Generate"}
            </Button>
          )}
        </Box>

        {loading && <LoadingOverlay />}
      </ModalBox>
    </Modal>
  );
};

export default AiGenerateEntryModal;
